import { useCallback, useEffect, useMemo, useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { DateTimePicker } from '@/components/DateTimePicker'
import { request } from '@/lib/request'
import { esQuery } from '@/lib/es'
import { toast } from 'sonner'
import {
  Activity,
  CircleDollarSign,
  Coins,
  RefreshCw,
  Target,
  TrendingUp,
} from 'lucide-react'

interface RechargeItem {
  recharge_time?: string
  plat?: string
  account?: string
  recharge_money?: number
  status?: string
}

interface DashboardData {
  plats?: Record<string, unknown>
  recharge?: RechargeItem[]
  rechargeStatus?: unknown[]
}

interface PayAggregationBucket {
  key: string
  win_loss_stats: {
    buckets: {
      wins: { doc_count: number }
      losses: { doc_count: number }
    }
  }
  total_earnings: { value: number }
  total_turnover: { value: number }
}

interface LuckAggregationBucket {
  key: string
  doc_count: number
  total_chip: { value: number }
}

interface AccountStatistic {
  account: string
  payWinRate: number
  payCount: number
  earnings: number
  turnover: number
  luckCount: number
  chip: number
}

const formatDateInput = (date: Date) => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const getDateRange = (dateValue: string) => {
  const start = new Date(`${dateValue}T00:00:00`)
  const end = new Date(start)
  end.setDate(end.getDate() + 1)
  return {
    gte: start.toISOString(),
    lt: end.toISOString(),
  }
}

const formatNumber = (value: number, fractionDigits = 2) =>
  value.toLocaleString('zh-CN', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })

const unwrapEsData = (result: unknown) => {
  if (!result || typeof result !== 'object') return null
  const response = result as { data?: unknown }
  return response.data ?? response
}

export default function Dashboard() {
  const [data, setData] = useState<DashboardData>({})
  const [statistics, setStatistics] = useState<AccountStatistic[]>([])
  const [selectedDate, setSelectedDate] = useState(() => formatDateInput(new Date()))
  const [loading, setLoading] = useState(false)

  const fetchDashboardData = useCallback(async () => {
    const [platsResp, rechargeStatusResp, rechargeResp] = await Promise.all([
      request('/common/apis/v1/count_run', {}),
      request('/common/apis/v1/get_recharge', {}),
      request('/system/api/query/recharge-log', {
        page_num: 1,
        page_size: 5,
        query_filter: {},
      }),
    ])

    setData({
      plats: platsResp.data,
      rechargeStatus: Array.isArray(rechargeStatusResp.data) ? rechargeStatusResp.data : [],
      recharge: rechargeResp.data?.list || [],
    })
  }, [])

  const fetchStatistics = useCallback(async (dateValue: string) => {
    const range = getDateRange(dateValue)
    const payQuery = {
      query: {
        bool: {
          filter: [
            { range: { timestamp: range } },
            { term: { mode: { value: 'run' } } },
          ],
        },
      },
      aggs: {
        accounts: {
          terms: { field: 'account.keyword', size: 2000 },
          aggs: {
            win_loss_stats: {
              filters: {
                filters: {
                  wins: {
                    script: {
                      script: "Double.parseDouble(doc['cur.keyword'].value) > 0",
                    },
                  },
                  losses: {
                    script: {
                      script: "Double.parseDouble(doc['cur.keyword'].value) < 0",
                    },
                  },
                },
              },
            },
            total_earnings: {
              sum: {
                script: {
                  source: "Double.parseDouble(doc['cur.keyword'].value)",
                },
              },
            },
            total_turnover: {
              sum: { field: 'bit' },
            },
          },
        },
      },
    }

    const luckQuery = {
      query: {
        range: {
          time: range,
        },
      },
      aggs: {
        accounts: {
          terms: { field: 't_name.keyword', size: 2000 },
          aggs: {
            total_chip: {
              sum: { field: 'chip' },
            },
          },
        },
      },
    }

    const [payResult, luckResult] = await Promise.all([
      esQuery('xr_pay_new', payQuery),
      esQuery('luck_data', luckQuery),
    ])

    const payData = unwrapEsData(payResult) as
      | { aggregations?: { accounts?: { buckets?: PayAggregationBucket[] } } }
      | null
    const luckData = unwrapEsData(luckResult) as
      | { aggregations?: { accounts?: { buckets?: LuckAggregationBucket[] } } }
      | null
    const payBuckets = payData?.aggregations?.accounts?.buckets || []
    const luckBuckets = luckData?.aggregations?.accounts?.buckets || []

    const merged = new Map<string, AccountStatistic>()
    payBuckets.forEach((bucket) => {
      const wins = bucket.win_loss_stats.buckets.wins.doc_count
      const losses = bucket.win_loss_stats.buckets.losses.doc_count
      const payCount = wins + losses
      merged.set(bucket.key, {
        account: bucket.key,
        payWinRate: payCount > 0 ? (wins / payCount) * 100 : 0,
        payCount,
        earnings: bucket.total_earnings.value || 0,
        turnover: bucket.total_turnover.value || 0,
        luckCount: 0,
        chip: 0,
      })
    })

    luckBuckets.forEach((bucket) => {
      const current = merged.get(bucket.key)
      merged.set(bucket.key, {
        account: bucket.key,
        payWinRate: current?.payWinRate || 0,
        payCount: current?.payCount || 0,
        earnings: current?.earnings || 0,
        turnover: current?.turnover || 0,
        luckCount: bucket.doc_count || 0,
        chip: bucket.total_chip.value || 0,
      })
    })

    setStatistics(
      Array.from(merged.values()).sort((left, right) => right.earnings - left.earnings),
    )
  }, [])

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      await Promise.all([fetchDashboardData(), fetchStatistics(selectedDate)])
    } catch (error) {
      console.error('Failed to load dashboard:', error)
      toast.error('加载 Dashboard 数据失败')
    } finally {
      setLoading(false)
    }
  }, [fetchDashboardData, fetchStatistics, selectedDate])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const totals = useMemo(
    () =>
      statistics.reduce(
        (result, item) => ({
          payCount: result.payCount + item.payCount,
          earnings: result.earnings + item.earnings,
          turnover: result.turnover + item.turnover,
          luckCount: result.luckCount + item.luckCount,
          chip: result.chip + item.chip,
        }),
        { payCount: 0, earnings: 0, turnover: 0, luckCount: 0, chip: 0 },
      ),
    [statistics],
  )

  return (
    <div className="h-full p-6 space-y-6 bg-background overflow-auto">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-primary to-blue-600 bg-clip-text text-transparent">
            Dashboard
          </h1>
          <p className="text-sm text-muted-foreground mt-1">账号统计日期：{selectedDate}</p>
        </div>
        <div className="flex items-center gap-2">
          <DateTimePicker
            value={new Date(`${selectedDate}T00:00:00`)}
            onChange={(date) => {
              if (date) {
                setSelectedDate(formatDateInput(date))
              }
            }}
            placeholder="选择统计日期"
          />
          <Button variant="outline" onClick={fetchData} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            刷新
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
        <SummaryCard
          title="投注次数"
          value={totals.payCount.toLocaleString('zh-CN')}
          description={`${statistics.length} 个账号`}
          icon={<Activity className="h-4 w-4 text-blue-500" />}
        />
        <SummaryCard
          title="总盈亏"
          value={formatNumber(totals.earnings)}
          valueClassName={totals.earnings >= 0 ? 'text-green-500' : 'text-red-500'}
          description="xr_pay_new / cur"
          icon={<TrendingUp className="h-4 w-4 text-green-500" />}
        />
        <SummaryCard
          title="总流水"
          value={formatNumber(totals.turnover)}
          description="xr_pay_new / bit"
          icon={<CircleDollarSign className="h-4 w-4 text-amber-500" />}
        />
        <SummaryCard
          title="幸运次数"
          value={totals.luckCount.toLocaleString('zh-CN')}
          description="luck_data 记录数"
          icon={<Target className="h-4 w-4 text-purple-500" />}
        />
        <SummaryCard
          title="筹码合计"
          value={formatNumber(totals.chip)}
          description="luck_data / chip"
          icon={<Coins className="h-4 w-4 text-cyan-500" />}
        />
      </div>

      <Card className="shadow-md border-border/50">
        <CardHeader>
          <CardTitle className="text-lg">账号统计</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>账号</TableHead>
                  <TableHead className="text-right">胜率</TableHead>
                  <TableHead className="text-right">投注次数</TableHead>
                  <TableHead className="text-right">盈亏</TableHead>
                  <TableHead className="text-right">流水</TableHead>
                  <TableHead className="text-right">幸运次数</TableHead>
                  <TableHead className="text-right">筹码</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {statistics.length > 0 ? (
                  <>
                    {statistics.map((item) => (
                      <TableRow key={item.account}>
                        <TableCell className="font-medium">{item.account}</TableCell>
                        <TableCell className="text-right">
                          {formatNumber(item.payWinRate)}%
                        </TableCell>
                        <TableCell className="text-right">
                          {item.payCount.toLocaleString('zh-CN')}
                        </TableCell>
                        <TableCell
                          className={`text-right font-medium ${
                            item.earnings >= 0 ? 'text-green-500' : 'text-red-500'
                          }`}
                        >
                          {formatNumber(item.earnings)}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatNumber(item.turnover)}
                        </TableCell>
                        <TableCell className="text-right">
                          {item.luckCount.toLocaleString('zh-CN')}
                        </TableCell>
                        <TableCell className="text-right">{formatNumber(item.chip)}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="bg-muted/50 font-semibold">
                      <TableCell>合计</TableCell>
                      <TableCell className="text-right">-</TableCell>
                      <TableCell className="text-right">
                        {totals.payCount.toLocaleString('zh-CN')}
                      </TableCell>
                      <TableCell
                        className={`text-right ${
                          totals.earnings >= 0 ? 'text-green-500' : 'text-red-500'
                        }`}
                      >
                        {formatNumber(totals.earnings)}
                      </TableCell>
                      <TableCell className="text-right">{formatNumber(totals.turnover)}</TableCell>
                      <TableCell className="text-right">
                        {totals.luckCount.toLocaleString('zh-CN')}
                      </TableCell>
                      <TableCell className="text-right">{formatNumber(totals.chip)}</TableCell>
                    </TableRow>
                  </>
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                      {loading ? '正在加载统计数据...' : '当前日期暂无统计数据'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 md:grid-cols-3">
        <SummaryCard
          title="平台总数"
          value={data.plats ? Object.keys(data.plats).length.toString() : '0'}
          description="count_run"
          icon={<Activity className="h-4 w-4 text-blue-500" />}
        />
        <SummaryCard
          title="充值状态"
          value={(data.rechargeStatus?.length || 0).toLocaleString('zh-CN')}
          description="当前充值记录数"
          icon={<CircleDollarSign className="h-4 w-4 text-yellow-500" />}
        />
        <SummaryCard
          title="统计账号"
          value={statistics.length.toLocaleString('zh-CN')}
          description="两个 ES 索引合并去重"
          icon={<Target className="h-4 w-4 text-purple-500" />}
        />
      </div>

      <Card className="shadow-md border-border/50">
        <CardHeader>
          <CardTitle className="text-lg">最近充值记录</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>时间</TableHead>
                <TableHead>平台</TableHead>
                <TableHead>账号</TableHead>
                <TableHead>金额</TableHead>
                <TableHead>状态</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.recharge && data.recharge.length > 0 ? (
                data.recharge.map((item, index) => (
                  <TableRow key={`${item.account || 'recharge'}-${index}`}>
                    <TableCell>{item.recharge_time || '--'}</TableCell>
                    <TableCell>{item.plat || '--'}</TableCell>
                    <TableCell>{item.account || '--'}</TableCell>
                    <TableCell>
                      {item.recharge_money === undefined
                        ? '--'
                        : formatNumber(item.recharge_money)}
                    </TableCell>
                    <TableCell>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          item.status === 'success'
                            ? 'bg-green-500/10 text-green-500'
                            : 'bg-yellow-500/10 text-yellow-500'
                        }`}
                      >
                        {item.status || '--'}
                      </span>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground">
                    暂无数据
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  )
}

function SummaryCard({
  title,
  value,
  description,
  icon,
  valueClassName = '',
}: {
  title: string
  value: string
  description: string
  icon: React.ReactNode
  valueClassName?: string
}) {
  return (
    <Card className="shadow-md hover:shadow-lg transition-shadow border-border/50">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <div className="h-8 w-8 rounded-lg bg-muted flex items-center justify-center">{icon}</div>
      </CardHeader>
      <CardContent>
        <div className={`text-2xl font-bold ${valueClassName}`}>{value}</div>
        <p className="text-xs text-muted-foreground">{description}</p>
      </CardContent>
    </Card>
  )
}
